import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { BundleGrid } from "../components/BundleGrid";
import { CollectionStrip } from "../components/CollectionStrip";
import { Stars } from "../components/Stars";
import { useSeo } from "../lib/seo";

const CATS = [
  { id: "all", label: "All", title: "Shop All", desc: "Browse every Christian ebook, prayer journal, scripture sticker pack and Bible study in the Faithful Store — instant digital downloads for a bolder daily walk." ,
    intro: "Everything in one place: ebooks in PDF and EPUB, guided journals you can print or fill on a tablet, scripture stickers for planners and Bibles, and studies for small groups or quiet mornings. Every purchase is delivered instantly and lives in your library forever." },
  { id: "ebook", label: "Ebooks", title: "Christian Ebooks", desc: "Christian ebooks in PDF and EPUB — honest, hope-filled reads on faith, prayer, courage and everyday discipleship. Instant download, read on any device.",
    intro: "Our ebooks are written for real life: short chapters, scripture woven through every page, and reflection prompts you can actually finish. Choose PDF for printing or EPUB for your e-reader — most titles include both." },
  { id: "journal", label: "Journals", title: "Prayer & Gratitude Journals", desc: "Printable Christian prayer journals, gratitude journals and 30-day guided devotional journals. Download, print at home or use on your tablet.",
    intro: "Guided pages for prayer, gratitude, scripture memory and sermon notes. Print the whole journal or just the pages you need; every layout is designed for US Letter and A4 and works beautifully in GoodNotes and Notability." },
  { id: "sticker", label: "Stickers", title: "Scripture Stickers", desc: "Printable scripture sticker packs for Bible journaling, planners and water bottles — bold verses in navy and gold, ready to print on any sticker paper.",
    intro: "High-resolution PNG and PDF sticker sheets with Bible verses, affirmations and seasonal designs. Print on full-sheet sticker paper, cut by hand or with a Cricut, and fill your planner with reminders of who God says you are." },
  { id: "study", label: "Studies", title: "Bible Studies", desc: "Downloadable Bible studies for individuals and small groups — week-by-week guides with discussion questions, prayer prompts and leader notes.",
    intro: "Each study walks through a book, theme or season of the church year with daily readings, discussion questions and a leader guide. Perfect for small groups, women's ministries, youth nights or a personal deep dive." },
  { id: "shipped", label: "Shipped Goods", kind: "physical", title: "Shipped Goods", desc: "Faith-filled mugs, prints, apparel and gifts shipped to your door — worldwide shipping by weight, sales tax calculated at checkout.",
    intro: "Real things for real homes: mugs, art prints, apparel and gift-ready pieces carrying the verses you love. Each item is packed with care and shipped worldwide; shipping is calculated by weight and tax at checkout." },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [ratings, setRatings] = useState({});
  useEffect(() => { api.get("/reviews/summary").then((r) => setRatings(r.data)); }, []);
  const current = CATS.find((c) => c.id === cat) || CATS[0];
  useSeo({ title: q ? `Search “${q}”` : current.title, description: current.desc, path: cat === "all" ? "/shop" : `/shop?cat=${cat}`, noindex: !!q,
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: `${current.title} | Faithful Store`, description: current.desc } });

  useEffect(() => {
    const p = {};
    if (current.kind) p.kind = current.kind;
    else if (cat && cat !== "all") p.category = cat;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => setProducts(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid md:grid-cols-3 gap-6 items-end mb-10">
        <div className="md:col-span-2">
          <div className="overline text-[#d4af37]">The Storefront</div>
          <h1 className="font-serif text-5xl mt-3 text-[#0a192f] tracking-tight">Every piece has a purpose.</h1>
        </div>
        <input
          data-testid="shop-search"
          placeholder="Search products…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="border border-[#e5e5e5] bg-white px-4 py-3 outline-none focus:border-[#0a192f]"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {CATS.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-${c.id}`}
            onClick={() => setParams(c.id === "all" ? {} : { cat: c.id })}
            className={`px-4 py-2 text-sm font-semibold border transition-colors ${
              cat === c.id ? "bg-[#0a192f] text-white border-[#0a192f]" : "bg-white text-[#0a192f] border-[#e5e5e5] hover:border-[#0a192f]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {!q && <p className="text-[#1a1a1a] leading-relaxed max-w-3xl -mt-4 mb-10" data-testid="shop-category-intro">{current.intro}</p>}

      {cat === "all" && !q && <><CollectionStrip /><BundleGrid /></>}

      {products.length === 0 ? (
        <div data-testid="shop-empty" className="card-flat p-16 text-center">
          <div className="font-serif text-2xl text-[#0a192f]">No products here yet.</div>
          <p className="text-[#4a4a4a] mt-3">Check back soon — new titles arriving every week.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p) => (
            <Link data-testid={`product-card-${p.id}`} key={p.id} to={`/product/${p.id}`} className="prod-card card-flat group block">
              <div className="aspect-[4/5] bg-[#f4f5f7] overflow-hidden">
                {p.cover_key ? (
                  <img src={fileUrl(p.cover_key)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#4a4a4a] font-serif text-3xl">{p.name?.[0]}</div>
                )}
              </div>
              <div className="p-5">
                <div className="overline text-[#4a4a4a]">{p.category}{p.kind === "physical" && <span className="ml-2 text-[#0a192f]">· ships to you</span>}</div>
                <div className="font-serif text-2xl text-[#0a192f] mt-2 leading-tight">{p.name}</div>
                <div className="text-sm text-[#4a4a4a] mt-2 line-clamp-2">{p.description}</div>
                {ratings[p.id] && <div className="mt-3 flex items-center gap-2 text-xs text-[#4a4a4a]" data-testid={`card-rating-${p.id}`}><Stars value={ratings[p.id].average} size="w-3.5 h-3.5" testId={`card-stars-${p.id}`} /> {ratings[p.id].average} ({ratings[p.id].count})</div>}
                <div className="mt-4 font-semibold text-[#0a192f] flex items-center gap-3">${(p.price_cents / 100).toFixed(2)}
                  {p.kind === "physical" && p.stock !== null && p.stock !== undefined && p.stock <= 0 && <span data-testid={`sold-out-${p.id}`} className="text-xs uppercase tracking-widest bg-[#f4f5f7] text-[#4a4a4a] px-2 py-0.5">Sold out</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
